import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Alert, } from '@thoughtspot/radiant-react/widgets/alert';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { getCurrentOrgId } from '@thoughtspot/session-service';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GitConfigsMappingStatus, } from "../../services/generated/graphql-types";
import { useGetGitConfig } from "../../services/version-control/version-control";
import { BasePageContext } from "../../contexts/basepage-context";
import { Horizontal } from "../../utils/layouts";
import { Header } from '../system/common';
import { AllOrgConfigurationDetails } from './configs-all-org/all-org-configuration-details';
import { ConfigurationDetails } from './configuration-details/configuration-details';
import { ConfigurationModal } from './configuration-modal/configuration-modal';
import { ALLORG, ModalActions, TranslationKeys } from './constant';
import { ResetConfiguration } from './reset-config-modal/reset-configuration';
import styles from './version-control.module.scss';
export const VersionControlSystem = ({ currentOrgId = 0, }) => {
    const { t } = useTranslation();
    const { metricsService } = BasePageContext();
    const [isConfiguarationModalOpen, setIsConfiguarationModalOpen] = useState(false);
    const [isConfigSuccess, setIsConfigSuccess] = useState(false);
    const [succeessMessage, setSuccessMessage] = useState('');
    const [configDetails, setConfigDetails] = useState([]);
    const [configToUpdate, setUpdatableConfig] = useState(null);
    const [resetConfigModalOrgId, setResetConfigModalOrgId] = useState('-1');
    const [newConfigOrgId, setNewConfigModalOrgId] = useState('-1');
    const [isLoading, setIsLoading] = useState(false);
    const isOfAllOrgType = useMemo(() => currentOrgId.toString() === ALLORG.ID, [currentOrgId]);
    const configFetchParams = {
        offset: -1,
        batchsize: -1,
        ...(!isOfAllOrgType && {
            org_identifiers: [getCurrentOrgId().toString()],
        }),
    };
    const { data: getConfigData, error: getConfigError, loading: configLoading, fetchGitConfig, } = useGetGitConfig(configFetchParams);
    const handleFetchConfig = () => {
        if (isOfAllOrgType) {
            fetchGitConfig({});
        }
        else {
            fetchGitConfig({
                org_identifiers: [getCurrentOrgId().toString()],
            });
        }
    };
    useEffect(() => {
        handleFetchConfig();
    }, []);
    useEffect(() => {
        if (getConfigData && !configLoading) {
            setIsLoading(false);
            setConfigDetails(getConfigData?.data.some(data => data.status === GitConfigsMappingStatus.Mapped)
                ? getConfigData.data
                : []);
        }
        if (getConfigError && !configLoading) {
            setIsLoading(false);
            setConfigDetails([]);
        }
        setIsLoading(configLoading);
    }, [getConfigData, getConfigError, configLoading]);
    const onModalCloseCb = useCallback((modalAction, isEditMode) => {
        if (modalAction === ModalActions.CONFIRM) {
            setIsLoading(true);
            setIsConfiguarationModalOpen(false);
            setIsConfigSuccess(true);
            setSuccessMessage(isEditMode
                ? t(TranslationKeys.VCS_ADMIN_EDIT_CONFIG_UPDATE_MESSAGE)
                : t(TranslationKeys.VCS_ADMIN_CREATE_CONFIG_SUCCESS_MESSAGE));
            handleFetchConfig();
        }
        else if (modalAction === ModalActions.CANCEL) {
            setIsConfiguarationModalOpen(false);
        }
        setUpdatableConfig(null);
        setNewConfigModalOrgId('-1');
    }, []);
    const onResetModalCloseCB = (modalAction) => {
        if (modalAction === ModalActions.CONFIRM) {
            setConfigDetails(prev => prev.filter(data => data.org.id.toString() !== resetConfigModalOrgId));
            setIsConfigSuccess(true);
            setSuccessMessage(t(TranslationKeys.VCS_ADMIN_RESET_SUCCESS_ALERT_TEXT));
            handleFetchConfig();
        }
        setResetConfigModalOrgId('-1');
    };
    const openOrgConfigEditModal = (orgId) => {
        if (isOfAllOrgType) {
            setUpdatableConfig(configDetails.find(data => data.org.id.toString() === orgId));
        }
        else {
            setUpdatableConfig(configDetails[0]);
        }
        setIsConfiguarationModalOpen(true);
    };
    const openCreateModalForSpecificOrg = (orgId) => {
        setIsConfiguarationModalOpen(true);
        setNewConfigModalOrgId(orgId);
    };
    const getOrgIdForModal = useMemo(() => {
        if (configToUpdate) {
            return configToUpdate.org.id.toString();
        }
        if (newConfigOrgId !== '-1') {
            return newConfigOrgId;
        }
        return isOfAllOrgType ? ALLORG.ID : getCurrentOrgId().toString();
    }, [configToUpdate, newConfigOrgId]);
    const handleConfigureClick = () => {
        setIsConfiguarationModalOpen(true);
        metricsService.collectEvent(new UserAction(userActionTypeEnum.VERSION_CONTROL_CLICK), {});
    };
    const primaryButton = {
        label: t('status.Enable'),
        onClick: handleConfigureClick,
    };
    const primaryActionForToast = {
        label: t('admin.refresh'),
        onClick: () => {
            window.location.reload();
        },
    };
    const renderSeperator = () => {
        return (React.createElement("div", { className: cx(styles.seperator, {
                [styles.seperator__fullWidth]: isOfAllOrgType,
            }) }));
    };
    return (React.createElement("div", { className: styles.vcsRightPanelContainer, "data-testid": "version-control-admin-page-body" },
        React.createElement(Header, { title: t(TranslationKeys.VCS_ADMIN_RIGHT_PANEL_TITLE), titleSize: "large", isFullWidth: true }),
        isLoading && (React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading, className: styles.fullScreenLoader },
            React.createElement(React.Fragment, null))),
        !isLoading && (React.createElement(React.Fragment, null,
            configDetails?.length > 0 ? (React.createElement(React.Fragment, null,
                renderSeperator(),
                isOfAllOrgType ? (React.createElement(AllOrgConfigurationDetails, { openConfigModalForEdit: openOrgConfigEditModal, onConfigReset: orgId => setResetConfigModalOrgId(orgId), onEnableVersionControl: openCreateModalForSpecificOrg })) : (React.createElement(ConfigurationDetails, { configDetails: configDetails, openConfigModalForEdit: openOrgConfigEditModal, onConfigReset: orgId => setResetConfigModalOrgId(orgId), collectEvent: metricsService.collectEvent })))) : (React.createElement(Vertical, { hAlignContent: "center", vAlignContent: "center", grow: true, "data-testid": "version-control-not-configured" },
                React.createElement(Horizontal, null,
                    React.createElement(Alert.Muted, { isVisible: true, title: t(TranslationKeys.VCS_ADMIN_MUTED_ALERT_TITLE), description: t(TranslationKeys.VCS_ADMIN_MUTED_ALERT_DESC), primaryAction: primaryButton, errorCode: ErrorCode.GLOBAL_EXPORT_SPOTAPPS, "data-testid": "success-muted-alert", global: true, imageVisible: true })))),
            isConfiguarationModalOpen && (React.createElement(ConfigurationModal, { isModalOpen: isConfiguarationModalOpen, setIsModalOpen: onModalCloseCb, isEditModal: configToUpdate !== null, configDetail: configToUpdate ?? null, currentOrgId: getOrgIdForModal, collectEvent: metricsService.collectEvent })),
            resetConfigModalOrgId && (React.createElement(ResetConfiguration, { isResetModalOpen: resetConfigModalOrgId !== '-1', onResetModalClose: onResetModalCloseCB, currentOrgId: resetConfigModalOrgId })),
            React.createElement(GlobalAlert.Toast, { isVisible: isConfigSuccess, message: succeessMessage, primaryAction: primaryActionForToast, onClose: () => setIsConfigSuccess(false) })))));
};
//# sourceMappingURL=version-control.container.js.map